import React, { useEffect, useState } from "react";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import { Link } from "react-router-dom";
import { Plus } from "feather-icons-react/build/IconComponents";
import Swal from "sweetalert2";
import { all_routes } from "../../../Router/all_routes";
import httpService from "../../../services/http/stores/outlet.service";
import usePagination, {
  createInitialPagination,
} from "../../../custom-hooks/usePagination";
import { useNavigate } from "react-router-dom";
import ConfirmationAlert from "../../../components/confirm-alert";
import UnlimitTable from "../../../components/ui-kit/unlimit-table";
import UnlimitBreadcrumb from "../../../components/ui-kit/unlimit-breadcrumb";
import { useTranslation } from "react-i18next";
import useRoleAndPerm from "../../../utils/permissionFunction";

const OutletsList = () => {
  const { hasPermission } = useRoleAndPerm();
  const [loading, setLoading] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [nameFilter, setNameFilter] = useState("");
  const [idFilter, setIdFilter] = useState("");
  const { t } = useTranslation();
  const initialPagination = createInitialPagination(t);
  const { paginationParams, updatePaginationParams } =
    usePagination(initialPagination);
  const navigate = useNavigate();

  const getOutletsList = async (
    pagination = paginationParams.pagination,
    filters,
    sorter = {}
  ) => {
    const data = {
      page: pagination?.current,
      per_page: pagination?.pageSize,
      ...filters,
      ...sorter,
    };

    setLoading(true);
    await httpService
      .outletList(data)
      .then((response) => {
        if (response.status === 200) {
          setOutlets(response.data.data?.data);
        }
        updatePaginationParams({
          pagination: {
            ...paginationParams.pagination,
            current: response.data.data.current_page,
            total: response.data.data.total,
            pageSize: response.data.data.per_page,
          },
        });
      })
      .catch((error) => {
        console.error("Error:", error);
        updatePaginationParams({
          pagination: {
            ...paginationParams.pagination,
          },
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    filters = {
      name: nameFilter,
      id: idFilter,
    };

    const nonNullFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => !!value)
    );

    if (sorter && sorter.field) {
      sorter = {
        sort_by: sorter.field,
        sort_direction: sorter.order === "ascend" ? "ASC" : "DESC",
      };
    }

    if (!pagination) {
      pagination = initialPagination;
    }

    await updatePaginationParams({
      pagination,
      filters,
      sorter,
    });

    await getOutletsList(pagination, nonNullFilters, sorter);
  };

  useEffect(() => {
    getOutletsList();
  }, []);

  const onConfirmDelete = async (id) => {
    await httpService
      .deleteOutlet(id)
      .then((response) => {
        if (response.status === 200) {
          Swal.fire({
            title: t("general.prompts.deleted"),
            text: t("shop.shopDeleteSuccess"),
            className: "btn btn-success",
            confirmButtonText: t("general.button.ok"),
            customClass: {
              confirmButton: "btn btn-success",
            },
          });
          getOutletsList();
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  const addNewOutlet = () => {
    navigate(all_routes.outletsCreate);
  };

  const columns = [
    {
      title: t("tableColumnName.srNo"),
      dataIndex: "index",
      align: "center",
      render: (value, item, index) =>
        (paginationParams.pagination?.current - 1) *
          paginationParams.pagination?.pageSize +
        (index + 1),
    },
    {
      title: t("tableColumnName.shopId"),
      dataIndex: "outlet_code",
      sorter: true,
    },
    {
      title: t("inputField.label.shopName"),
      dataIndex: "name",
      render: (text, record) => (
        <div className="d-flex align-items-center">
          <ImageWithBasePath
            type="link"
            src={record.image}
            alt="Outlet"
            style={{ width: 30, height: 30, marginRight: 10 }}
          />
          {record.name}
        </div>
      ),
    },
    {
      title: t("tableColumnName.contactName"),
      dataIndex: "owner_name",
      sorter: true,
    },
    {
      title: t("tableColumnName.phone"),
      dataIndex: "phone",
    },
    {
      title: t("tableColumnName.email"),
      dataIndex: "email",
      sorter: true,
    },
    {
      title: t("tableColumnName.status"),
      dataIndex: "is_active",
      render: (text, record) => {
        if (record.is_active == 1) {
          return (
            <span className="badge badge-linesuccess">
              <a>{t("general.actions.active")}</a>
            </span>
          );
        } else {
          return (
            <span className="badge badge-linedanger">
              <a>{t("general.actions.deactive")}</a>
            </span>
          );
        }
      },
    },
    {
      title: t("tableColumnName.actions"),
      key: "actions",
      align: "center",
      render: (text, record) => (
        <div className="action-table-data" style={{ minWidth: "140px" }}>
          <div className="edit-delete-action">
            {hasPermission([
              "shelf-management-edit",
              "shelf-management-view",
            ]) && (
              <a
                className="p-2"
                onClick={() => {
                  if (hasPermission(["shelf-management-edit"])) {
                    navigate(`/outlets/${record.id}/shelf-management-edit`, {
                      state: { name: record.name, image: record.image },
                    });
                  } else {
                    navigate(`/outlets/${record.id}/shelf-management-view`, {
                      state: { name: record.name, image: record.image },
                    });
                  }
                }}
              >
                <i className="feather-align-justify"></i>
              </a>
            )}
            <a
              className="p-2"
              onClick={() => navigate(`/outlets/${record.id}/details`)}
            >
              <i className="feather-eye"></i>
            </a>
            {hasPermission(["shop-edit"]) && (
              <a
                className="p-2"
                onClick={() => navigate(`/outlets/${record.id}/edit`)}
              >
                <i className="feather-edit-2 text-black"></i>
              </a>
            )}
            {hasPermission(["shop-delete"]) && (
              <ConfirmationAlert onConfirm={() => onConfirmDelete(record.id)} />
            )}
          </div>
        </div>
      ),
    },
  ];

  const breadcrumbItems = [
    { label: "", link: "/", icon: "feather-home" },
    { label: t("shop.shop"), active: true },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex flex-column">
              <div className="page-title">
                <h4>{t("shop.shopList")}</h4>
              </div>
              <UnlimitBreadcrumb items={breadcrumbItems} />
            </div>

            {hasPermission(["shop-add"]) && (
              <div className="page-btn">
                <button onClick={addNewOutlet} className="btn btn-added">
                  <Plus className="me-2 iconsize" />
                  {t("general.button.addNew")}
                </button>
              </div>
            )}
          </div>

          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-top row">
                <div className="col-lg-5 col-sm-5 col-12">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder={t("inputField.placeholder.enterID")}
                      className="form-control form-control-sm formsearch-custom ps-2"
                      value={idFilter}
                      onChange={(e) => setIdFilter(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleTableChange();
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="col-lg-5 col-sm-5 col-12">
                  <input
                    type="text"
                    placeholder={t("inputField.placeholder.enterShopName")}
                    className="form-control form-control-sm formsearch-custom ps-2"
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleTableChange();
                      }
                    }}
                  />
                </div>
                <div className="col-lg-2 col-sm-2 col-12">
                  <div className="input-blocks mb-0">
                    <button
                      className="btn btn-filters col-auto ms-auto px-2"
                      onClick={handleTableChange}
                    >
                      <i data-feather="search" className="feather-search m-0" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="table-responsive">
                <UnlimitTable
                  pagination={paginationParams.pagination}
                  columns={columns}
                  dataSource={outlets}
                  rowKey={(record) => record.id}
                  loading={loading}
                  onChange={handleTableChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutletsList;
